import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { reportService } from '../services/api';
import { Report } from '../types';
import {
  Box, Paper, Typography, Button, Container, Grid, Dialog, DialogTitle,
  DialogContent, DialogActions, Divider, Alert, CircularProgress, TextField,
  FormControl, InputLabel, Select, MenuItem, Stack, Chip, IconButton, Tooltip,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';

const maskToken = (token: string) =>
  token.length > 4 ? '●'.repeat(token.length - 4) + token.slice(-4) : '●'.repeat(token.length);

const maskOwner = (owner: string) =>
  owner.length > 3 ? owner.slice(0, owner.length - 3) + '●●●' : '●●●';

const STATUS_OPTIONS = ['Active', 'Inactive', 'Changed'] as const;
type Status = typeof STATUS_OPTIONS[number];

const statusColor = (s: string) =>
  s === 'Active' ? 'success' : s === 'Inactive' ? 'default' : 'warning';

interface FormData {
  category: string;
  owner: string;
  token: string;
  content: string;
  old_token: string;
  status: Status;
}

const EMPTY_FORM: FormData = { category: '', owner: '', token: '', content: '', old_token: '', status: 'Active' };

const ReportPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState<FormData>(EMPTY_FORM);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [tokenRevealed, setTokenRevealed] = useState(false);
  const [ownerRevealed, setOwnerRevealed] = useState(false);
  useEffect(() => {
    if (id === 'new') { setEditing(true); setLoading(false); }
    else if (id) { fetchReport(); }
  }, [id]);

  const fetchReport = async () => {
    if (!id) return;
    try {
      const response = await reportService.getById(id);
      const r = response.data;
      setReport(r);
      setFormData({
        category: r.category,
        owner: r.owner || '',
        token: r.token || '',
        content: r.content || '',
        old_token: r.old_token || '',
        status: r.status || 'Active',
      });
    } catch (err: any) {
      setError(err.message || 'Failed to load entry');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (id === 'new') {
        await reportService.create(formData);
        navigate('/');
      } else {
        await reportService.update(id!, formData);
        setEditing(false);
        fetchReport();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save entry');
    }
  };

  const handleArchive = async () => {
    const newStatus = report?.status === 'Inactive' ? 'Active' : 'Inactive';
    try {
      await reportService.update(id!, { status: newStatus });
      setArchiveOpen(false);
      fetchReport();
    } catch (err: any) {
      setError(err.message || 'Failed to archive entry');
    }
  };

  const handleDelete = async () => {
    try {
      await reportService.delete(id!);
      navigate('/');
    } catch (err: any) {
      setError(err.message || 'Failed to delete entry');
    }
  };

  const set = (field: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setFormData(prev => ({ ...prev, [field]: e.target.value }));

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !report) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 8, textAlign: 'center' }}>
          <Paper sx={{ p: 4 }}>
            <Typography variant="h4" color="error" gutterBottom>Error</Typography>
            <Typography variant="body1" color="text.secondary" paragraph>{error}</Typography>
            <Button variant="contained" onClick={() => { setError(''); fetchReport(); }} sx={{ mt: 2 }}>Try Again</Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  const readonlyField = (label: string, value: string) => (
    <TextField fullWidth label={label} value={value} InputProps={{ readOnly: true }}
      variant="filled" size="small" sx={{ '& .MuiFilledInput-root': { bgcolor: 'action.hover' } }} />
  );

  const editForm = (
    <Paper sx={{ p: 4 }}>
      <Grid container spacing={3}>

        {/* 1. Category */}
        <Grid item xs={12} sm={4}>
          <TextField fullWidth label="Category" placeholder="Enter category"
            inputProps={{ maxLength: 30 }}
            helperText={`${formData.category.length}/30`}
            value={formData.category} onChange={set('category')} />
        </Grid>

        {/* 2. Owner */}
        <Grid item xs={12} sm={4}>
          <TextField fullWidth label="Owner" placeholder="Owner name"
            inputProps={{ maxLength: 30 }}
            helperText={`${formData.owner.length}/30`}
            value={formData.owner} onChange={set('owner')} />
        </Grid>

        {/* 3. Token */}
        <Grid item xs={12} sm={4}>
          <TextField fullWidth label="Token" placeholder="Token value"
            inputProps={{ maxLength: 30 }}
            helperText={`${formData.token.length}/30`}
            value={formData.token} onChange={set('token')} />
        </Grid>

        {/* 4. Content */}
        <Grid item xs={12}>
          <TextField fullWidth multiline rows={6} label="Content" placeholder="Enter content..."
            value={formData.content} onChange={set('content')} />
        </Grid>

        {/* 5. Creator (read-only) */}
        <Grid item xs={12} sm={6}>
          {readonlyField('Creator', id === 'new' ? (user?.name || '') : (report?.creator_name || ''))}
        </Grid>

        {/* 6. Date Created (read-only) */}
        <Grid item xs={12} sm={6}>
          {readonlyField('Date Created', id === 'new' ? 'Auto on save' : new Date(report!.created_at).toLocaleString())}
        </Grid>

        {/* 7. Last Modified (read-only) */}
        <Grid item xs={12} sm={6}>
          {readonlyField('Last Modified', id === 'new' ? 'Auto on save' : (report?.date_modified ? new Date(report.date_modified).toLocaleString() : '-'))}
        </Grid>

        {/* 8. Old Token */}
        <Grid item xs={12} sm={3}>
          <TextField fullWidth label="Old Token" placeholder="Previous token"
            inputProps={{ maxLength: 30 }}
            helperText={`${formData.old_token.length}/30`}
            value={formData.old_token} onChange={set('old_token')} />
        </Grid>

        {/* 9. Status */}
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select value={formData.status} label="Status"
              onChange={(e: SelectChangeEvent) => setFormData(p => ({ ...p, status: e.target.value as Status }))}>
              {STATUS_OPTIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>

      </Grid>
    </Paper>
  );

  if (id === 'new') {
    return (
      <Container maxWidth="md">
        <Box sx={{ py: 4 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
            <Typography variant="h4">Create New Entry</Typography>
            <Box>
              <Button variant="outlined" onClick={() => navigate('/')} sx={{ mr: 1 }}>Cancel</Button>
              <Button variant="contained" onClick={handleSave} startIcon={<SaveIcon />}>Save</Button>
            </Box>
          </Stack>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {editForm}
        </Box>
      </Container>
    );
  }

  if (!report) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 8, textAlign: 'center' }}>
          <Typography variant="h4" color="text.secondary" gutterBottom>Entry Not Found</Typography>
          <Button variant="contained" onClick={() => navigate('/')} sx={{ mt: 2 }}>Back to Dashboard</Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
          <div>
            <Button variant="text" onClick={() => navigate('/')} startIcon={<ArrowBackIcon />}>
              Back to Dashboard
            </Button>
            <Typography variant="h4" sx={{ mt: 2 }}>
              {editing ? 'Edit Entry' : 'Entry Details'}
            </Typography>
          </div>
          {!editing && (
            <Box>
              <Button variant="outlined" onClick={() => setEditing(true)} startIcon={<EditIcon />} sx={{ mr: 1 }}>Edit</Button>
              <Button variant="outlined" onClick={() => setArchiveOpen(true)} sx={{ mr: 1 }}
                startIcon={report?.status === 'Inactive' ? <UnarchiveIcon /> : <ArchiveIcon />}
                color={report?.status === 'Inactive' ? 'primary' : 'warning'}>
                {report?.status === 'Inactive' ? 'Restore' : 'Archive'}
              </Button>
              <Button variant="outlined" onClick={() => setDeleteOpen(true)} startIcon={<DeleteIcon />} color="error">Delete</Button>
            </Box>
          )}
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {editing ? editForm : (
          <Paper sx={{ p: 4 }}>
            <Grid container spacing={2}>

              {/* 1. Category */}
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">Category</Typography>
                <Typography variant="body1"><strong>{report.category}</strong></Typography>
              </Grid>

              {/* 2. Owner */}
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">Owner</Typography>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace', letterSpacing: ownerRevealed ? 'normal' : '2px' }}>
                    {report.owner ? (ownerRevealed ? report.owner : maskOwner(report.owner)) : '-'}
                  </Typography>
                  {report.owner && (
                    <Tooltip title={ownerRevealed ? 'Hide' : 'Show'}>
                      <IconButton size="small" onClick={() => setOwnerRevealed(v => !v)}>
                        {ownerRevealed ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </Grid>

              {/* 3. Token */}
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">Token</Typography>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                    {report.token ? (tokenRevealed ? report.token : maskToken(report.token)) : '-'}
                  </Typography>
                  {report.token && (
                    <Tooltip title={tokenRevealed ? 'Hide' : 'Reveal'}>
                      <IconButton size="small" onClick={() => setTokenRevealed(v => !v)}>
                        {tokenRevealed ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </Grid>

              <Grid item xs={12}><Divider /></Grid>

              {/* 4. Content */}
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Content</Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                  {report.content || '-'}
                </Typography>
              </Grid>

              <Grid item xs={12}><Divider /></Grid>

              {/* 5. Creator */}
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Creator</Typography>
                <Typography variant="body1">{report.creator_name || '-'}</Typography>
              </Grid>

              {/* 6. Date Created */}
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Date Created</Typography>
                <Typography variant="body1">{new Date(report.created_at).toLocaleString()}</Typography>
              </Grid>

              {/* 7. Last Modified */}
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Last Modified</Typography>
                <Typography variant="body1">
                  {report.date_modified ? new Date(report.date_modified).toLocaleString() : '-'}
                </Typography>
              </Grid>

              {/* 8. Old Token */}
              <Grid item xs={12} sm={3}>
                <Typography variant="caption" color="text.secondary">Old Token</Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>{report.old_token || '-'}</Typography>
              </Grid>

              {/* 9. Status */}
              <Grid item xs={12} sm={3}>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <Box mt={0.5}>
                  <Chip label={report.status || 'Active'} size="small"
                    color={statusColor(report.status || 'Active') as any} />
                </Box>
              </Grid>

            </Grid>
          </Paper>
        )}

        {editing && (
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="outlined" onClick={() => { setEditing(false); setError(''); }} startIcon={<CancelIcon />} sx={{ mr: 1 }}>Cancel</Button>
            <Button variant="contained" onClick={handleSave} startIcon={<SaveIcon />}>Save</Button>
          </Box>
        )}

        <Dialog open={archiveOpen} onClose={() => setArchiveOpen(false)} maxWidth="sm">
          <DialogTitle>{report?.status === 'Inactive' ? 'Restore Entry' : 'Archive Entry'}</DialogTitle>
          <Divider />
          <DialogContent>
            <Typography>
              {report?.status === 'Inactive'
                ? 'Restore this entry to Active status?'
                : 'Archive this entry? It will be set to Inactive.'}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setArchiveOpen(false)}>Cancel</Button>
            <Button onClick={handleArchive} variant="contained"
              color={report?.status === 'Inactive' ? 'primary' : 'warning'}>
              {report?.status === 'Inactive' ? 'Restore' : 'Archive'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="sm">
          <DialogTitle>Confirm Delete</DialogTitle>
          <Divider />
          <DialogContent>
            <Typography>Are you sure you want to delete this entry? This cannot be undone.</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
            <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default ReportPage;
